import React, { useEffect, useState } from 'react';
import { EventQuestionnaire, Question } from '../../data/questionnaire';
import { saveQuestionnaire, deleteQuestionnaire } from '../../services/questionnaireService';
import {
    Box,
    Button,
    Typography,
    List,
    ListItem,
    IconButton,
    Divider,
    Collapse,
    Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddEditQuestionModal from './AddEditQuestionModal';
import ConfirmationDialog from './ConfirmationDialog';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

interface Props {
    questionnaire: EventQuestionnaire | null;
    isEditable: boolean;
    setQuestionnaire: (q: EventQuestionnaire | null) => void;
    eventId: string;
    onBack?: () => void;
    forceOpenModal?: boolean;
    onModalClose?: () => void;
}

const QuestionnaireSection: React.FC<Props> = ({ questionnaire, isEditable, setQuestionnaire, onBack, eventId, forceOpenModal, onModalClose }) => {
    const [questions, setQuestions] = useState<Question[]>(questionnaire?.questions || []);
    const [editingQuestion, setEditingQuestion] = useState<Question | undefined>(undefined);
    const [showModal, setShowModal] = useState(false);
    const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    useEffect(() => {
        if (questionnaire?.questions) {
            setQuestions(questionnaire.questions);
        }
    }, [questionnaire]);

    useEffect(() => {
        if (forceOpenModal && isEditable) {
            setShowModal(true);
            setEditingQuestion(undefined);
            onModalClose?.();
        }
    }, [forceOpenModal, isEditable, onModalClose]);

    const handleDelete = async (index: number) => {
        try {
            const updatedQuestions = questions.filter((_, i) => i !== index);
            const updated: EventQuestionnaire = { eventId, questions: updatedQuestions };

            const saved = await saveQuestionnaire(eventId, updated);

            setQuestions(saved.questions);
            setQuestionnaire(saved);
        } catch (error) {
            console.error("Error deleting question:", error);
            alert("Failed to delete question. Please try again.");
        } finally {
            setConfirmDeleteIndex(null);
        }
    };

    const handleDeleteAll = async () => {
        try {
            const success = await deleteQuestionnaire(eventId);
            if (success) {
                setQuestionnaire(null);
                setQuestions([]);
                setConfirmDeleteAll(false);
            } else {
                console.error("Failed to delete questionnaire.");
            }
        } catch (error) {
            console.error("Error deleting questionnaire:", error);
            alert("Failed to delete questionnaire. Please try again.");
        }
    };

    const toggleExpand = (index: number) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    };

    return (
        <Box>
            {onBack && (
                <Button
                    variant="contained"
                    onClick={onBack}
                    sx={{ mb: 2, backgroundColor: '#1976d2 !important', color: '#fff !important' }}
                >
                    Back to Event Details
                </Button>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">Questions</Typography>
                {isEditable && (
                    <Box>
                        <Button
                            onClick={() => setShowModal(true)}
                            variant="contained"
                            sx={{ backgroundColor: '#2e7d32 !important', color: '#fff !important' }}
                        >
                            Add Question
                        </Button>
                        {questionnaire && (
                            <Button
                                onClick={() => setConfirmDeleteAll(true)}
                                variant="contained"
                                sx={{ ml: 2, backgroundColor: '#d32f2f !important', color: '#fff !important' }}
                            >
                                Delete Questionnaire
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <List>
                {questions.map((q, index) => (
                    <Box key={q.id || index}>
                        <ListItem
                            button
                            onClick={() => toggleExpand(index)}
                            secondaryAction={
                                isEditable && (
                                    <>
                                        <IconButton onClick={() => { setEditingQuestion(q); setShowModal(true); }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => setConfirmDeleteIndex(index)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </>
                                )
                            }
                        >
                            <Typography>{q.prompt} ({q.type})</Typography>
                            {expandedIndex === index ? <ExpandLess sx={{ ml: 1 }} /> : <ExpandMore sx={{ ml: 1 }} />}
                        </ListItem>

                        <Collapse in={expandedIndex === index} timeout="auto" unmountOnExit>
                            <Box
                                ml={4}
                                mt={1}
                                mb={2}
                                p={2}
                                bgcolor="#f5f5f5"
                                borderRadius={2}
                                boxShadow={1}
                            >
                                <Stack spacing={1}>
                                    <Typography variant="body2">
                                        <strong>Required:</strong> <span style={{ color: '#555' }}>{q.isRequired ? 'Yes' : 'No'}</span>
                                    </Typography>

                                    {q.type === 'MultipleChoice' && (
                                        <Typography variant="body2">
                                            <strong>Allows Multiple Answers:</strong> <span style={{ color: '#555' }}>{q.allowMultiAnswer ? 'Yes' : 'No'}</span>
                                        </Typography>
                                    )}

                                    {(q.type === 'MultipleChoice' || q.type === 'SimpleChoice') &&
                                        (q.options?.length ?? 0) > 0 && (
                                            <>
                                                <Typography variant="body2"><strong>Options:</strong></Typography>
                                                <Stack spacing={0.5} ml={2}>
                                                    {(q.options ?? []).map((opt, i) => (
                                                        <Typography key={i} variant="body2" color="text.secondary">
                                                            • {opt}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </>
                                        )}
                                </Stack>
                            </Box>
                        </Collapse>
                    </Box>
                ))}
            </List>

            {showModal && (
                <AddEditQuestionModal
                    eventId={eventId}
                    question={editingQuestion}
                    allQuestions={questions}
                    onClose={() => {
                        setShowModal(false);
                        setEditingQuestion(undefined);
                    }}
                    onUpdate={(updated) => {
                        setQuestions(updated);
                        setQuestionnaire({ eventId, questions: updated });
                    }}
                />
            )}

            {confirmDeleteIndex !== null && (
                <ConfirmationDialog
                    message="Are you sure you want to delete this question?"
                    onConfirm={() => handleDelete(confirmDeleteIndex)}
                    onCancel={() => setConfirmDeleteIndex(null)}
                />
            )}

            {confirmDeleteAll && (
                <ConfirmationDialog
                    message="Are you sure you want to delete the entire questionnaire? This cannot be undone."
                    onConfirm={handleDeleteAll}
                    onCancel={() => setConfirmDeleteAll(false)}
                />
            )}
        </Box>
    );
};

export default QuestionnaireSection;